import type { Event } from '@sentry/core';

/** Aligned with analytics host allowlist in app/layout.tsx */
const SENTRY_ALLOWED_HOSTS = new Set(['exaltride.com', 'www.exaltride.com']);

function normalizeHostname(host: string): string {
  return host.split(':')[0].toLowerCase().trim();
}

export function isSentryAllowedPublicHost(hostname: string): boolean {
  return SENTRY_ALLOWED_HOSTS.has(normalizeHostname(hostname));
}

function hostFromRequestUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    return normalizeHostname(new URL(url).hostname);
  } catch {
    return undefined;
  }
}

function hostFromRequestHeaders(
  headers: Record<string, string> | undefined,
): string | undefined {
  if (!headers) return undefined;
  const entry = Object.entries(headers).find(
    ([key]) => key.toLowerCase() === 'host',
  );
  const raw = entry?.[1];
  return raw ? normalizeHostname(raw) : undefined;
}

export function getHostFromSentryEvent(event: Event): string | undefined {
  return (
    hostFromRequestUrl(event.request?.url) ??
    hostFromRequestHeaders(event.request?.headers)
  );
}

/**
 * Server/edge: send only if the request targets an allowed public host, or the
 * event has no request context and we are on Vercel production (e.g. some startup paths).
 */
export function shouldSendSentryEventForCurrentDeployment(event: Event): boolean {
  const host = getHostFromSentryEvent(event);
  if (host) return isSentryAllowedPublicHost(host);
  return process.env.VERCEL_ENV === 'production';
}

/** Use for Node server and Edge Sentry.init `enabled`. */
export function isSentryEnabledForProductionDeployment(): boolean {
  if (process.env.NODE_ENV !== 'production') return false;
  if (
    process.env.VERCEL_ENV === 'preview' ||
    process.env.VERCEL_ENV === 'development'
  ) {
    return false;
  }
  return true;
}
